import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  type TouchEvent as ReactTouchEvent,
} from 'react';
import { Loader2, X } from 'lucide-react';

/**
 * Small, local UI kit.
 *
 * Every colour comes from the token layer (`bg-surface`, `text-fg-muted`, …) so
 * dark mode needs no `dark:` variants anywhere. A raw hex or a `dark:` class in
 * a component is a bug, not a shortcut.
 *
 * Phone first, since that is where the approvals now happen: controls clear the
 * 44px touch floor, dialogs arrive from the bottom edge, and a table that would
 * need sideways scrolling becomes a stack of cards instead (see `DataTable`).
 */

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** Below this the layout is a phone: one column, cards, bottom sheets. */
export const PHONE_QUERY = '(max-width: 767px)';

/** Tracks a media query. Used for the handful of decisions CSS cannot make —
 *  which element to render, not merely how to paint it. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

export function useIsPhone(): boolean {
  return useMediaQuery(PHONE_QUERY);
}

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  busy?: boolean;
  size?: 'sm' | 'md';
  /** Full width on phones, natural width from `md:` up. The usual shape for a
   *  form's primary action, where a 90px button in a 360px row reads as an
   *  afterthought. */
  block?: boolean;
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-fg hover:opacity-90 active:opacity-75',
  secondary:
    'border border-border bg-surface text-fg hover:bg-surface-muted active:bg-surface-muted',
  ghost: 'text-fg-muted hover:text-fg hover:bg-surface-muted active:bg-surface-muted',
  danger: 'bg-danger text-white hover:opacity-90 active:opacity-75',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    busy = false,
    size = 'md',
    block = false,
    className,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || busy}
      className={cx(
        // `touch-manipulation` drops the double-tap-to-zoom wait on the tap.
        'inline-flex touch-manipulation items-center justify-center gap-2 rounded-lg font-medium transition disabled:opacity-55',
        // 44px on touch, back to a desktop-sized control from `md:` up.
        size === 'sm'
          ? 'min-h-11 px-3 text-sm md:min-h-0 md:px-2.5 md:py-1.5 md:text-xs'
          : 'min-h-11 px-4 text-sm md:min-h-0 md:px-3.5 md:py-2',
        block && 'w-full md:w-auto',
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {busy && <Loader2 size={14} className="animate-spin" />}
      {children}
    </button>
  );
});

/**
 * An icon-only control with a real hit area.
 *
 * The header used bare `p-2` buttons around 18px icons — a 34px target, under
 * the 44px floor, which on a phone is the difference between "opens the menu"
 * and "opens whatever was next to the menu".
 */
export const IconButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
  function IconButton({ className, children, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        className={cx(
          'text-fg-muted hover:text-fg hover:bg-surface-muted active:bg-surface-muted inline-flex size-11 shrink-0 touch-manipulation items-center justify-center rounded-lg transition md:size-9',
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

export function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="text-fg mb-1.5 block text-sm font-medium">
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-fg-muted mt-1 text-xs">{hint}</p>}
      {error && (
        <p role="alert" className="text-danger mt-1 text-xs">
          {error}
        </p>
      )}
    </div>
  );
}

// The 16px floor that keeps iOS from zooming on focus is set once, globally, in
// tokens.css — repeating it per control is how one input gets missed.
const CONTROL =
  'w-full min-h-11 rounded-lg border border-border bg-bg px-3 py-2 text-sm text-fg outline-none transition focus:border-primary disabled:opacity-60 md:min-h-0';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cx(CONTROL, className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cx(CONTROL, className)} {...rest}>
        {children}
      </select>
    );
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx(CONTROL, 'min-h-24 resize-y', className)} {...rest} />;
});

/**
 * Does this class string already state a padding? Variants and `!` included, so
 * `md:p-0` and `!px-2` count as the caller having decided.
 *
 * A string test rather than a prop, because the alternative — `padded={false}`
 * on every site that already passes its own padding — is 65 edits across two
 * dozen live worktrees, and the point of this change is to stop relying on
 * anyone remembering. This way no call site changes at all.
 */
const PADDING_CLASS = /(?:^|\s)!?(?:[a-z0-9.-]+:)*p[xytrbl]?-/;

/** Exported for its test — the whole behaviour of `Card` turns on this regex. */
export function statesPadding(className: string | undefined): boolean {
  return PADDING_CLASS.test(className ?? '');
}

/**
 * A surface. It brings its own padding now.
 *
 * It did not, and there is no reading of that which was ever right: fifty call
 * sites on main passed no padding, and every one of them rendered with the text
 * against the border. Three screens were repaired one at a time before it was
 * obvious that the primitive was the bug, not the pages — architecture rule 1,
 * "doğru olan yapısal olsun, disiplinle sağlanmasın".
 *
 * A caller that states any padding of its own still wins, including `p-0` for
 * the cases that genuinely want none — a card wrapping something that carries
 * its own frame, like `Table`.
 */
export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cx(
        'border-border bg-surface rounded-xl border',
        !statesPadding(className) && 'px-4 py-3',
        className,
      )}
    >
      {children}
    </div>
  );
}

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'primary';

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-muted text-fg-muted',
  success: 'bg-success/15 text-success',
  warning: 'bg-warning/15 text-warning',
  danger: 'bg-danger/15 text-danger',
  primary: 'bg-primary/15 text-primary',
};

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-fg text-xl font-semibold sm:text-2xl">{title}</h1>
        {description && <p className="text-fg-muted mt-1 text-sm">{description}</p>}
      </div>
      {/* Wraps rather than overflows: three actions in a 360px row are three
          rows, not one row clipped at the edge. */}
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Spinner({ label = 'Yükleniyor' }: { label?: string }) {
  return (
    <div className="text-fg-muted flex items-center justify-center gap-2 py-12 text-sm">
      <Loader2 size={16} className="animate-spin" />
      {label}…
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="text-fg-muted px-4 py-12 text-center text-sm">
      <p className="text-fg font-medium">{title}</p>
      {hint && <p className="mt-1">{hint}</p>}
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="border-danger/30 bg-danger/10 text-danger rounded-lg border px-3 py-2 text-sm"
    >
      {message}
    </div>
  );
}

/**
 * A dialog on a desktop, a bottom sheet on a phone.
 *
 * The centred `max-w-lg` box was the same element at every width: on a 360px
 * screen it sat under the notch, its footer buttons landed behind the keyboard,
 * and there was no gesture to dismiss it. Here the phone case is a real sheet —
 * it arrives from the bottom edge, it has a handle you can drag down, its body
 * scrolls on its own, and it keeps clear of the home indicator.
 *
 * `max-h-[92dvh]` and not `92vh`: with a keyboard open on Android, `vh` still
 * measures the screen the keyboard is covering.
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  wide = false,
  footer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  /**
   * Pinned below the scrolling body rather than at the end of it.
   *
   * With the actions inside the body, a form long enough to scroll puts its
   * own submit button off the bottom of the sheet — and a phone keyboard makes
   * every form long enough to scroll. Here "Kaydet" is where it was when the
   * field was tapped.
   */
  footer?: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<Element | null>(null);
  const [dragY, setDragY] = useState(0);
  const dragStart = useRef<number | null>(null);
  const isPhone = useIsPhone();

  // Escape closes; body scroll is locked while open. Both are the kind of thing
  // that is invisible when present and glaring when missing.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  // Focus moves into the sheet and comes back where it started. Without the
  // second half, closing a dialog on a phone leaves the focus ring at the top
  // of the document and the next Tab starts the page over.
  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement;
    const timer = window.setTimeout(() => {
      const target = panelRef.current?.querySelector<HTMLElement>(
        'input:not([type=hidden]), textarea, select, [autofocus]',
      );
      (target ?? panelRef.current)?.focus();
    }, 0);
    return () => {
      window.clearTimeout(timer);
      const previous = restoreFocusTo.current;
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [open]);

  useEffect(() => {
    if (open) setDragY(0);
  }, [open]);

  if (!open) return null;

  // Drag-to-dismiss, from the header only — dragging from the body would fight
  // with scrolling it.
  const dragHandlers = isPhone
    ? {
        onTouchStart: (e: ReactTouchEvent) => {
          dragStart.current = e.touches[0]?.clientY ?? null;
        },
        onTouchMove: (e: ReactTouchEvent) => {
          if (dragStart.current === null) return;
          const dy = (e.touches[0]?.clientY ?? 0) - dragStart.current;
          setDragY(dy > 0 ? dy : 0);
        },
        onTouchEnd: () => {
          // Past a third of the way down it is a dismissal, not a fidget.
          if (dragY > 110) onClose();
          else setDragY(0);
          dragStart.current = null;
        },
      }
    : {};

  return (
    <div
      className="animate-fade-in fixed inset-0 z-50 flex items-end justify-center bg-black/50 md:items-start md:overflow-y-auto md:p-4 md:pt-[10vh]"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        style={dragY > 0 ? { transform: `translateY(${dragY}px)`, transition: 'none' } : undefined}
        className={cx(
          'border-border bg-surface flex max-h-[92dvh] w-full flex-col rounded-t-2xl border-t shadow-xl outline-none',
          // `md:rounded-t-xl` is not redundant next to `md:rounded-xl`: the
          // base `rounded-t-2xl` is a longhand and would otherwise keep the
          // desktop dialog's top corners rounder than its bottom ones.
          'animate-sheet-in md:animate-none md:max-h-[82dvh] md:rounded-xl md:rounded-t-xl md:border',
          wide ? 'md:max-w-3xl' : 'md:max-w-lg',
        )}
      >
        <div className="shrink-0 md:hidden" {...dragHandlers}>
          <div className="flex justify-center pt-2.5 pb-1">
            <span className="bg-border h-1 w-10 rounded-full" />
          </div>
        </div>

        <div
          className="border-border flex shrink-0 items-center justify-between gap-2 border-b px-4 py-2.5 md:px-5 md:py-3"
          {...dragHandlers}
        >
          <h2 className="text-fg min-w-0 truncate text-base font-semibold">{title}</h2>
          <IconButton onClick={onClose} aria-label="Kapat">
            <X size={18} />
          </IconButton>
        </div>

        {/* The body scrolls, not the dialog: the action row stays reachable
            after the keyboard has taken half the screen. */}
        <div
          className={cx(
            'overscroll-pane flex-1 overflow-y-auto px-4 py-4 md:px-5',
            footer ? 'pb-4' : 'pb-safe-4 md:pb-4',
          )}
        >
          {children}
        </div>

        {footer && (
          <div className="border-border bg-surface pb-safe-4 shrink-0 border-t px-4 py-3 md:px-5 md:pb-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A table that scrolls rather than squeezes.
 *
 * `minWidth` matters more than it looks: without a floor the columns compress
 * to fit, the last one gets clipped, and no scrollbar appears to say so — the
 * actions simply are not there. Screens with more columns raise it.
 *
 * On a phone, prefer `DataTable`, which turns the same data into cards. Reach
 * for this one directly only for genuinely grid-shaped data (a month of
 * attendance, a payment week) where sideways scrolling is the honest answer.
 */
export function Table({
  children,
  minWidth = '36rem',
  fixed = false,
}: {
  children: ReactNode;
  minWidth?: string;
  /**
   * Honour `<colgroup>` widths instead of sizing columns by their content.
   *
   * Off by default, because content-sized columns are right for most tables
   * here. Turn it on when a table declares widths — without it the browser
   * treats a `<col>` width as a hint and the widest cell still decides, which
   * looks exactly like the widths being ignored. Cells then have to be able to
   * clip (`truncate`, `line-clamp`), since fixed layout will not grow a column
   * to fit its text.
   */
  fixed?: boolean;
}) {
  // `overscroll-pane-x`, `overscroll-pane` DEĞİL: dikey ekseni kapatmak bu
  // kutuda sayfa kaydırmasını yutuyordu — gerekçesi tokens.css'te yazılı.
  return (
    <div className="border-border overscroll-pane-x overflow-x-auto rounded-xl border">
      <table className={cx('w-full text-sm', fixed && 'table-fixed')} style={{ minWidth }}>
        {children}
      </table>
    </div>
  );
}

export function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <th
      className={cx(
        'bg-surface-muted text-fg-muted border-border border-b px-3 py-2 text-left text-xs font-semibold tracking-wide uppercase',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  className,
  colSpan,
}: {
  children?: ReactNode;
  className?: string;
  colSpan?: number;
}) {
  return (
    <td
      colSpan={colSpan}
      className={cx('border-border text-fg border-b px-3 py-2 align-middle', className)}
    >
      {children}
    </td>
  );
}

/**
 * Tabs that survive a narrow screen.
 *
 * The hand-rolled version — a flex row of buttons under a border — silently
 * clipped its third and fourth tab at 360px, with nothing on screen to suggest
 * anything had been cut. This one scrolls, snaps, and hides the scrollbar.
 */
export function Tabs<K extends string>({
  value,
  onChange,
  items,
  className,
}: {
  value: K;
  onChange: (key: K) => void;
  items: ReadonlyArray<{ key: K; label: string; count?: number }>;
  className?: string;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  /**
   * SEÇİLİ SEKME GÖRÜNÜR KALIYOR.
   *
   * Şerit kayıyor ama kendiliğinden kaymıyordu: adresle gelinen sekme
   * (`?sekme=…`) sağda, ekranın dışındaysa telefonda hiçbir sekme seçili
   * görünmüyordu — 375px'te Duyurular'ın altıncı sekmesi 577px'te başlıyor.
   * Eski `/duyurular/kategoriler` yer imi ve arama kutusu tam oraya iniyor.
   *
   * `scrollIntoView` DEĞİL: o, sekmeyi göstermek için sayfayı da dikey
   * kaydırabilir. Yalnız şeridin kendi yatay konumu oynatılıyor; sekme zaten
   * görünüyorsa hiçbir şey yapılmıyor, yani masaüstünde etkisi yok.
   *
   * Şeridin İÇERİĞİ değişince de bakılıyor, yalnız seçim değişince değil:
   * sayılar ilk çizimden SONRA geliyor (Duyurular'ın rozetleri ayrı bir
   * istek) ve soldaki bir sekmeye eklenen sayı, yerine oturmuş seçili sekmeyi
   * yeniden dışarı itiyor — ölçüldü: «Zamanlanmış 1» geldiğinde «Kategoriler»
   * 375px'lik ekranda 382px'te bitti. Bağımlılık dizinin kendisi DEĞİL
   * imzası: satır içi yazılan `items` her çizimde yeni bir dizi, ve elle
   * kaydırılmış şerit her tuş vuruşunda geri çekilmemeli.
   */
  const icerik = items.map((item) => `${item.key}:${item.label}:${item.count ?? ''}`).join('|');
  useEffect(() => {
    const list = listRef.current;
    const active = list?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    if (!list || !active) return;
    const kutu = list.getBoundingClientRect();
    const sekme = active.getBoundingClientRect();
    // Şeridin telefondaki `px-4` iç boşluğu kadar pay: sekme kenara yapışmasın.
    const pay = 16;
    if (sekme.right > kutu.right) list.scrollLeft += sekme.right - kutu.right + pay;
    else if (sekme.left < kutu.left) list.scrollLeft -= kutu.left - sekme.left + pay;
  }, [value, icerik]);

  return (
    <div
      ref={listRef}
      role="tablist"
      className={cx(
        'border-border -mx-4 mb-4 flex gap-1 overflow-x-auto border-b px-4 md:mx-0 md:px-0',
        '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {items.map((item) => {
        const active = item.key === value;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.key)}
            className={cx(
              '-mb-px flex min-h-11 shrink-0 touch-manipulation items-center gap-1.5 border-b-2 px-3 text-sm whitespace-nowrap transition',
              active
                ? 'border-primary text-fg font-medium'
                : 'text-fg-muted hover:text-fg border-transparent',
            )}
          >
            {item.label}
            {item.count !== undefined && item.count > 0 && (
              <span
                className={cx(
                  'rounded-full px-1.5 py-0.5 text-[11px] leading-none font-semibold',
                  active ? 'bg-primary text-primary-fg' : 'bg-surface-muted text-fg-muted',
                )}
              >
                {item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  /**
   * The card's title line on a phone: rendered large and without its label.
   * Exactly one column should set this; without it the first column is used.
   */
  primary?: boolean;
  /** Left off the phone card. For columns that only earn their space on a wide
   *  screen — an internal id, a duplicated company name, a filler dash. */
  desktopOnly?: boolean;
  align?: 'left' | 'right';
  className?: string;
}

/**
 * One column definition, two layouts.
 *
 * A six-column table at 360px is not a table — it is a 36rem canvas behind a
 * horizontal scrollbar, with the decision buttons parked off the right edge
 * where nobody scrolls to find them. That is the single reason approving a
 * leave request from a phone did not work.
 *
 * From `md:` up this renders the same table it always did. Below it, each row
 * becomes a card: the primary column as a heading, the rest as label/value
 * pairs, and the actions as full-width buttons at the bottom where a thumb
 * already is.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  actions,
  minWidth = '36rem',
  empty,
  onRowClick,
}: {
  columns: ReadonlyArray<Column<T>>;
  rows: readonly T[];
  rowKey: (row: T) => string;
  /** Rendered in the last table cell, and as a full-width row on the card. */
  actions?: (row: T) => ReactNode;
  minWidth?: string;
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
}) {
  if (rows.length === 0 && empty) return <>{empty}</>;

  const primary = columns.find((c) => c.primary) ?? columns[0];
  const rest = columns.filter((c) => c !== primary && !c.desktopOnly);

  return (
    <>
      {/* Phone: cards */}
      <div className="space-y-2 md:hidden">
        {rows.map((row) => {
          const body = (
            <>
              <div className="text-fg text-[15px] leading-snug font-medium">
                {primary?.cell(row)}
              </div>
              {rest.length > 0 && (
                <dl className="mt-2 space-y-1">
                  {rest.map((col) => {
                    const value = col.cell(row);
                    if (value === null || value === undefined || value === '') return null;
                    return (
                      <div key={col.key} className="flex items-start justify-between gap-3">
                        <dt className="text-fg-muted shrink-0 text-xs">{col.header}</dt>
                        <dd className="text-fg min-w-0 text-right text-sm">{value}</dd>
                      </div>
                    );
                  })}
                </dl>
              )}
            </>
          );

          return (
            <Card key={rowKey(row)} className="p-3.5">
              {onRowClick ? (
                <button
                  type="button"
                  onClick={() => onRowClick(row)}
                  className="w-full touch-manipulation text-left"
                >
                  {body}
                </button>
              ) : (
                body
              )}
              {actions && (
                <div className="border-border mt-3 flex flex-wrap items-center gap-2 border-t pt-3 [&>*]:flex-1">
                  {actions(row)}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Desktop: the table, unchanged */}
      <div className="hidden md:block">
        <Table minWidth={minWidth}>
          <thead>
            <tr>
              {columns.map((col) => (
                <Th key={col.key} className={cx(col.align === 'right' && 'text-right')}>
                  {col.header}
                </Th>
              ))}
              {actions && <Th className="text-right">İşlem</Th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cx(onRowClick && 'hover:bg-surface-muted/60 cursor-pointer')}
              >
                {columns.map((col) => (
                  <Td
                    key={col.key}
                    className={cx(col.align === 'right' && 'text-right', col.className)}
                  >
                    {col.cell(row)}
                  </Td>
                ))}
                {actions && (
                  <Td className="text-right whitespace-nowrap">
                    <div
                      className="inline-flex gap-1"
                      onClick={(e) => e.stopPropagation()}
                      role="presentation"
                    >
                      {actions(row)}
                    </div>
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </>
  );
}

/**
 * A checkbox with a row you can actually hit.
 *
 * A bare 16px `<input type="checkbox">` next to a `text-sm` label is a 16px
 * target — a third of the 44px floor, and the one control where a miss silently
 * toggles the wrong thing. Wrapping it in the label makes the whole row the
 * target, which is also how every native list on a phone behaves.
 */
export function Checkbox({
  label,
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: ReactNode }) {
  return (
    <label
      className={cx(
        'text-fg flex min-h-11 cursor-pointer touch-manipulation items-center gap-3 text-sm select-none',
        className,
      )}
    >
      <input
        type="checkbox"
        className="accent-primary size-5 shrink-0 rounded md:size-4"
        {...rest}
      />
      <span className="min-w-0">{label}</span>
    </label>
  );
}

/**
 * `1.250,50` is what a Turkish keyboard types, and `<input type="number">`
 * throws it away.
 *
 * The comma is not a valid character for a numeric input, so the browser
 * reports `value === ''` — not the text the person can plainly see in the box.
 * The amount is silently zero, or the form refuses with "zorunlu" over a filled
 * field. Every money field in this app had that bug.
 *
 * So: a text input with `inputMode="decimal"` — which still raises the numeric
 * keypad on a phone, comma and all — and a parser that accepts both separators.
 */
export const DecimalInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>
>(function DecimalInput({ className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      className={cx(CONTROL, className)}
      {...rest}
    />
  );
});

/**
 * Reads a human-typed amount. Returns null for anything that is not one, so a
 * caller can tell "empty" from "zero" — a distinction `Number('')` loses.
 */
export function parseDecimal(raw: string): number | null {
  const text = raw.trim();
  if (text === '') return null;

  const lastComma = text.lastIndexOf(',');
  const lastDot = text.lastIndexOf('.');
  // Whichever separator comes last is the decimal one; the other is grouping.
  // '1.250,50' -> comma decides, '1,250.50' -> dot does, '1250,50' -> comma.
  let normalised: string;
  if (lastComma > lastDot) normalised = text.replace(/\./g, '').replace(',', '.');
  else if (lastDot > lastComma) normalised = text.replace(/,/g, '');
  else normalised = text;

  const value = Number(normalised);
  return Number.isFinite(value) ? value : null;
}

/**
 * The row of filters above a list.
 *
 * Written by hand on every screen as a flex row of `w-44` / `w-56` selects, it
 * is the second most reliable source of sideways scrolling after the tables.
 * Two columns on a phone, a wrapping row from `md:` up; children size
 * themselves to the cell rather than to a pixel guess.
 *
 * The stretch rule skips checkboxes and radios. It used to catch them too and
 * outrank `Checkbox`'s `size-5`: the box grew to the full cell and pushed its
 * label past the card, so every list with a toggle in its filter row scrolled
 * sideways at 375px — and so did every bottom sheet opened over it, since a
 * fixed overlay takes the widened viewport. Three screens had already moved
 * their toggles out of the bar to get away from it.
 */
export function FilterBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Card
      className={cx(
        'mb-4 grid grid-cols-2 gap-2 p-3 md:flex md:flex-wrap md:items-end md:gap-3',
        '[&_input:not([type=checkbox]):not([type=radio])]:w-full [&_select]:w-full',
        'md:[&_input:not([type=checkbox]):not([type=radio])]:w-auto md:[&_select]:w-auto',
        className,
      )}
    >
      {children}
    </Card>
  );
}
